import { Button, Card, Input } from '@ohear/ui'
import { Link, createFileRoute, useSearch } from '@tanstack/react-router'
import { useState } from 'react'
import { z } from 'zod'
import { ApiError, api } from '../lib/api'

/**
 * Self-serve actor signup.
 *
 * Producer and supervisor accounts are still invitation-only — this page
 * exists for the actor / talent side. Posts {email, name} to
 * `/v1/auth/signup-actor`, which creates the auth user, mirrors them into
 * `public.users` with `primary_role='actor'`, and provisions a personal
 * "Workspace" org owned by the actor (so the org-scoped middleware has
 * somewhere to land them before any producer invites them to a real
 * project).
 *
 * On success we render the same "check your email" copy as the login page;
 * the magic link drops the actor on /auth/callback → / .
 *
 * Search params:
 *   ?email= — pre-fill (e.g. when arriving from a marketing CTA that already
 *             collected the email).
 */
const signupSearchSchema = z.object({
  email: z.string().email().optional(),
})

export const Route = createFileRoute('/signup')({
  component: SignupPage,
  validateSearch: signupSearchSchema,
})

interface SignupResponse {
  ok: true
  message: string
}

function SignupPage() {
  const search = useSearch({ from: '/signup' })
  const [email, setEmail] = useState(search.email ?? '')
  const [name, setName] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [resultMessage, setResultMessage] = useState<string | null>(null)

  if (resultMessage) {
    return (
      <Card className="w-full max-w-[460px] mx-auto my-8 sm:my-16" style={{ maxWidth: 460 }}>
        <h1 style={{ marginTop: 0 }}>Check your email</h1>
        <p>{resultMessage}</p>
        <p style={{ color: '#6b7280', fontSize: 13 }}>The link expires in 60 minutes.</p>
        <Button
          variant="ghost"
          className="w-full sm:w-auto"
          style={{ marginTop: 12 }}
          onClick={() => {
            setResultMessage(null)
            setError(null)
          }}
        >
          Try a different email
        </Button>
      </Card>
    )
  }

  return (
    <Card className="w-full max-w-[460px] mx-auto my-8 sm:my-16" style={{ maxWidth: 460 }}>
      <h1 style={{ marginTop: 0 }}>Sign up as an actor</h1>
      <p style={{ color: '#6b7280' }}>
        Create an OHEAR.AI actor account. Producers can then invite you to projects, and your
        completed sessions appear in one place.
      </p>
      <form
        onSubmit={async (e) => {
          e.preventDefault()
          setError(null)
          setSubmitting(true)
          try {
            const res = await api<SignupResponse>('/api/v1/auth/signup-actor', {
              method: 'POST',
              body: JSON.stringify({ email, name }),
            })
            setResultMessage(res.message)
          } catch (err) {
            if (err instanceof ApiError) setError(err.message)
            else setError(err instanceof Error ? err.message : 'Signup failed')
          } finally {
            setSubmitting(false)
          }
        }}
      >
        <label style={{ display: 'block', fontSize: 13, marginBottom: 6 }}>Full name</label>
        <Input
          required
          autoComplete="name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Alice Actor"
          disabled={submitting}
          className="w-full"
          minLength={2}
          maxLength={120}
        />

        <label style={{ display: 'block', fontSize: 13, margin: '14px 0 6px' }}>Email</label>
        <Input
          type="email"
          required
          autoComplete="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@example.com"
          disabled={submitting}
          className="w-full"
        />

        {error ? <p style={{ color: '#b91c1c', fontSize: 13, marginTop: 8 }}>{error}</p> : null}

        <div style={{ marginTop: 18 }}>
          <Button
            type="submit"
            disabled={submitting || !email || name.length < 2}
            className="w-full sm:w-auto"
          >
            {submitting ? 'Creating account...' : 'Create account'}
          </Button>
        </div>
      </form>

      <p style={{ color: '#6b7280', fontSize: 12, marginTop: 20 }}>
        Already have an account?{' '}
        <Link to="/login" style={{ color: '#1f2937' }}>
          Sign in
        </Link>
      </p>
      <p style={{ color: '#6b7280', fontSize: 12, marginTop: 8 }}>
        By signing up, you agree to our{' '}
        <a
          href={`${import.meta.env.VITE_MARKETING_URL ?? 'https://ohear.ai'}/legal/terms`}
          target="_blank"
          rel="noreferrer"
          style={{ color: '#1f2937' }}
        >
          Terms
        </a>{' '}
        and{' '}
        <a
          href={`${import.meta.env.VITE_MARKETING_URL ?? 'https://ohear.ai'}/legal/privacy`}
          target="_blank"
          rel="noreferrer"
          style={{ color: '#1f2937' }}
        >
          Privacy Policy
        </a>
        .
      </p>
    </Card>
  )
}
